import { useState } from "react";
import { CircularProgress } from "@mui/material";
import { isAddress } from "ethers";

import { LandToken } from "../../types/LandToken";
import LandActionPopup, { PopupAddressPill, PopupCTA, PopupFootnote } from "../LandPage/LandActionPopup";

type LandTransferModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (newGuardianAddress: string) => void;
  loading: boolean;
  error?: string | null;
  land: LandToken | null;
};

// Transfer-guardian, on the land-card + purple-panel shell the offer / bid popups use (Figma
// node 3002:4045, with an address field in place of the amount pill). The rows are what the
// old frosted panel listed; NEW GUARDIAN is the caption over the field rather than a figure,
// which is why the shell's heading value is optional.
const LandTransferModal = ({ open, onClose, onConfirm, loading, error, land }: LandTransferModalProps) => {
  const [newGuardianAddress, setNewGuardianAddress] = useState("");

  const address = newGuardianAddress.trim();
  // ethers' isAddress accepts an all-lowercase address and a correctly checksummed one, and
  // rejects mixed case with a bad checksum — the case a typo most often produces. The old
  // panel enabled the CTA on any non-empty string and left the contract to revert.
  const addressValid = address.length > 0 && isAddress(address);

  const handleConfirm = () => {
    onConfirm(address);
  };

  const handleClose = () => {
    // A transfer in flight can't be dismissed — the wallet prompt is still up. The parent's
    // handleTransferModalClose carries the same guard and its own message for it.
    if (!loading) {
      setNewGuardianAddress("");
      onClose();
    }
  };

  // const isContractActive = () => {
  //   if (!land?.etNft?.easementActivated) {
  //     return false;
  //   }

  //   const endContract = moment(land.etNft.easementActivated.contractStart).add(
  //     land.meta?.contractPeriod || 1,
  //     TERM_UNIT
  //   );
  //   return !endContract.isBefore(moment());
  // };

  return (
    <LandActionPopup
      open={open}
      onClose={handleClose}
      token={land ?? undefined}
      // statLabel="NEW GUARDIAN"
      // rows={[
      //   {
      //     label: "EcoScore",
      //     value: land?.ecoScore !== undefined ? formatValue(land.ecoScore * 100, true, 2) : "N/A",
      //   },
      //   {
      //     label: "Mint capacity",
      //     value: land?.etNft?.totalTreeForEasement
      //       ? sixDigits(formatValue(land.etNft.totalTreeForEasement, true, 3)) + " TR3"
      //       : "N/A",
      //   },
      //   {
      //     label: isContractActive() ? "Active time" : "Contract period",
      //     value: isContractActive() ? (
      //       <CountdownTimer
      //         medium
      //         sx={popupTimerSx}
      //         targetDate={moment(land?.etNft?.easementActivated?.contractStart)
      //           .add(land?.meta?.contractPeriod || 1, TERM_UNIT)
      //           .toDate()}
      //       />
      //     ) : (
      //       `${land?.meta?.contractPeriod || 0} ${
      //         (land?.meta?.contractPeriod || 0) > 1 ? TERM_UNIT : TERM_UNIT.slice(0, -1)
      //       }`
      //     ),
      //   },
      // ]}
    >
      <PopupAddressPill
        value={newGuardianAddress}
        onChange={(e) => setNewGuardianAddress(e.target.value)}
        variant="outlined"
        placeholder="New Guardian Address 0x…"
        disabled={loading}
        // Only flag the field once something has been typed — an untouched empty field
        // showing a red ring on open reads as a failure the user did not cause.
        error={address.length > 0 && !addressValid}
        inputProps={{ spellCheck: false, autoCapitalize: "off", autoCorrect: "off" }}
      />
      <PopupCTA onClick={handleConfirm} disabled={loading || !addressValid} variant="contained">
        {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Confirm transfer"}
      </PopupCTA>

      {/* The parent's transaction error (a revert, a missing contract) or its can't-close
          notice. Rendered in the pill's outline red so the two invalid states agree. */}
      {error && <PopupFootnote tone="error">{error}</PopupFootnote>}

      <PopupFootnote>
        This action will initiate a transfer of your land ownership. Once transferred, you will no longer have guardian
        rights to this land.
      </PopupFootnote>
    </LandActionPopup>
  );
};

export default LandTransferModal;
